import { useEffect, useState } from 'react'
import {
  collectionGroup,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import { db } from '@/firebase'

interface SiteInfo {
  id: string
  name: string
}

interface SiteManager {
  uid: string
  email: string
  name: string
  siteId: string
  siteName: string
}

const useSites = () => {
  const [sites, setSites] = useState<SiteInfo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const fetchSites = async () => {
      const shadowQuery = collectionGroup(db, 'shadow')
      const snap = await getDocs(shadowQuery)
      if (cancelled) return

      const result: SiteInfo[] = snap.docs
        .map((doc) => {
          const data = doc.data()
          return {
            id: data.id || '',
            name: data.name || data.id || ''
          }
        })
        .filter((s) => s.id)
        .sort((a, b) => a.name.localeCompare(b.name))

      setSites(result)
      setLoading(false)
    }

    fetchSites().catch((err) => {
      console.error('Failed to fetch sites', err)
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [])

  return { sites, loading }
}

const useManagersForSites = (sites: SiteInfo[]) => {
  const [managers, setManagers] = useState<SiteManager[]>([])
  const [loading, setLoading] = useState(false)

  const key = sites
    .map((s) => s.id)
    .sort()
    .join(',')

  useEffect(() => {
    if (sites.length === 0) {
      setManagers([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    fetchManagersForSites(sites)
      .then((result) => {
        if (cancelled) return
        setManagers(result)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Failed to fetch site managers', err)
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [key])

  return { managers, loading }
}

const fetchManagersForSites = async (
  sites: SiteInfo[]
): Promise<SiteManager[]> => {
  const perSite = await Promise.all(
    sites.map(async (site) => {
      const adminCol = collection(db, `sites/${site.id}/admin`)
      const q = query(adminCol, where('role', 'in', ['owner', 'admin']))
      const snap = await getDocs(q)
      return snap.docs.map((doc) => {
        const data = doc.data()
        return {
          uid: doc.id,
          email: data.email || '',
          name: data.name || '',
          siteId: site.id,
          siteName: site.name
        } satisfies SiteManager
      })
    })
  )

  return perSite.flat()
}

export { useSites, useManagersForSites, fetchManagersForSites }
export type { SiteInfo, SiteManager }
